/**
 * AuthShell — the page-level chrome shared by every Authentication page.
 *
 * Wraps an `<AppHeader>` over a vertically-centered content column.
 * Children (the CenteredCard with the form + an optional footer Row)
 * land in that column.
 *
 * Kept local to Authentication/ because the auth pages are the only
 * surface in the system that wants the
 *   full-viewport dark canvas + empty AppHeader + centered narrow column
 * combo. If a second feature ever needs the same shape, this graduates
 * into the composition primitive layer.
 *
 * @format
 */

import {type ReactNode} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {AppHeader} from 'ui/components/organisms/AppHeader';

const Page = styled(Box)(({theme}) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  paddingBottom: theme.spacing(15),
  backgroundColor: theme.palette.background.default,
}));

// `flex-direction: column` keeps the card and the optional footer stacked
// vertically; `alignItems: center` horizontally centers each child; each
// child caps its own maxWidth so they line up by their card edges.
const FormColumn = styled('main')({
  flex: '1 1 0',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
});

export interface AuthShellProps {
  /** Wired to the AppHeader logo click — typically navigates to `/`. */
  onLogoClick?: () => void;
  /** Card + footer go here. They sit on the centered column. */
  children?: ReactNode;
}

export function AuthShell({onLogoClick, children}: AuthShellProps) {
  return (
    <Page>
      <AppHeader variant='empty' onLogoClick={onLogoClick} />
      <FormColumn>{children}</FormColumn>
    </Page>
  );
}
